import { isFunction } from "~/tools/is-function";
import type { Record } from "~/tools/record";
import { toJSON } from "~/tools/to-json";

import type { StreamOutput } from "./output";
import { type StreamConfig, StreamRegistry } from "./registry";
import { StreamStore } from "./store";

/**
 * The configurator makes it a bit easier to configure a stream,
 * because the TStreamType inferred automatically and the listen signature is configured manually.
 */
class StreamConfigurator<
	TStreamType extends string,
	TSchema extends Record<string, StreamManagerConvert<unknown, unknown, unknown>>,
> {
	public constructor(
		private readonly streamType: TStreamType,
		private readonly manager: StreamManager<TSchema>,
	) {}

	/**
	 * Assign the convert function to the stream type.
	 * This method defines the input and output types of the stream.
	 */
	public listen<TMessage, TError, TData>(
		convert: StreamManagerConvert<TMessage, TError, TData>,
	): StreamManager<TSchema & Record<TStreamType, StreamManagerConvert<TMessage, TError, TData>>> {
		return this.manager._merge(this.streamType, convert);
	}
}

export type StreamManagerConvert<TMessage, TError, TData> = (
	message: TMessage,
	current: [null | TError, null | TData],
) => [null | TError, null | TData];

type StreamOutputFromConvert<TConvert> =
	TConvert extends StreamManagerConvert<unknown, infer TError, infer TData> ? StreamOutput<TError, TData> : never;

type StreamMessageFromConvert<TConvert> =
	TConvert extends StreamManagerConvert<infer TMessage, unknown, unknown> ? TMessage : never;

type StreamConfigFromSchema<TSchema> = StreamConfig<Extract<keyof TSchema, string>>;

export type StreamOutputFromManager<TStreamType extends string, TManager> =
	TManager extends StreamManager<infer TSchema> ? StreamOutputFromConvert<TSchema[TStreamType]> : never;

export type StreamMessageFromManager<TStreamType extends string, TManager> =
	TManager extends StreamManager<infer TSchema> ? StreamMessageFromConvert<TSchema[TStreamType]> : never;

export type StreamConfigFromManager<TManager> =
	TManager extends StreamManager<infer TSchema> ? StreamConfigFromSchema<TSchema> : never;

/**
 * The stateless WebSocket stream manager.
 * The main goal of the class is to provide a type-safe way to manage WebSocket streams.
 * It allows to specify the schema of the streams and their message types,
 * so the StreamStore and StreamRegistry inherit the schema and can be used in a type-safe way.
 */
export class StreamManager<TSchema extends Record<string, StreamManagerConvert<unknown, unknown, unknown>>> {
	/**
	 * A static method to initialize the stream configurator with empty schema.
	 */
	public static stream<TStreamType extends string>(
		streamType: TStreamType,
	): StreamConfigurator<TStreamType, Record<never, never>> {
		return new StreamManager({}).stream(streamType);
	}

	private constructor(private readonly schema: TSchema) {}

	/**
	 * The internal method to merge the current schema with a new stream type.
	 */
	public _merge<TStreamType extends string, TMessage, TError, TData>(
		streamType: TStreamType,
		convert: StreamManagerConvert<TMessage, TError, TData>,
	): StreamManager<TSchema & Record<TStreamType, StreamManagerConvert<TMessage, TError, TData>>> {
		return new StreamManager({ ...this.schema, [streamType]: convert });
	}

	/**
	 * Initializes a stream configurator with the current schema.
	 */
	public stream<TStreamType extends string>(streamType: TStreamType): StreamConfigurator<TStreamType, TSchema> {
		return new StreamConfigurator(streamType, this);
	}

	/**
	 * Initializes the StreamStore and StreamRegistry with the current schema,
	 * so they will be used by the configured manager in a type-safe way.
	 */
	public manage(): {
		store: StreamStore<StreamManager<TSchema>>;
		registry: StreamRegistry<StreamManager<TSchema>>;
	} {
		return {
			store: StreamStore.empty(this),
			registry: StreamRegistry.init(this),
		};
	}

	/**
	 * A proxy method to get the stream output from the related store.
	 */
	public get<TStream extends StreamConfigFromSchema<TSchema>>(
		stream: TStream,
		store: StreamStore<this>,
	): StreamOutputFromConvert<TSchema[TStream["streamType"]]>;
	public get(stream: StreamConfig, store: StreamStore<this>): StreamOutput<unknown, unknown> {
		return store.get(toJSON(stream));
	}

	/**
	 * A proxy method to update the stream output in the related store.
	 * Applies the convert function from the schema to the message.
	 */
	public receive<TStream extends StreamConfigFromSchema<TSchema>>(
		stream: TStream,
		message: StreamMessageFromConvert<TSchema[TStream["streamType"]]>,
		store: StreamStore<this>,
	): StreamStore<this>;
	public receive(stream: StreamConfig, message: unknown, store: StreamStore<this>): StreamStore<this> {
		const convert = this.schema[stream.streamType];

		if (!isFunction(convert)) {
			return store;
		}

		return store.update(toJSON(stream), (current) => convert(message, current));
	}

	/**
	 * A proxy method to subscribe to the stream in the related registry.
	 */
	public subscribe<TStream extends StreamConfigFromSchema<TSchema>>(
		stream: TStream,
		registry: StreamRegistry<this>,
	): StreamRegistry<this> {
		return registry.add(toJSON(stream), stream as unknown as StreamConfigFromManager<this>);
	}

	/**
	 * A proxy method to unsubscribe from the stream in the related registry.
	 */
	public unsubscribe<TStream extends StreamConfigFromSchema<TSchema>>(
		stream: TStream,
		registry: StreamRegistry<this>,
	): StreamRegistry<this> {
		return registry.remove(toJSON(stream));
	}
}
